import { getLocalDateString, parseISODate } from '@core/dateUtils.js';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import "./date-picker.css";

/**
 * Reusable configuration and initialization of Flatpickr in range mode
 * with French localization
 */

// Base configuration for Flatpickr in range mode
const FLATPICKR_RANGE_CONFIG_FR = {
    mode: "range",
    dateFormat: "d.m.Y",
    locale: "fr",
    allowInput: false,
    clickOpens: true
};

/**
 * Initializes a Flatpickr in range mode on an element
 *
 * @param {string|HTMLElement} selector - CSS selector or DOM element of the input
 * @param {Object} options - Configuration options (optional)
 * @param {string|Array} options.defaultDate - Default dates in "YYYY-MM-DD to YYYY-MM-DD" format or ["YYYY-MM-DD", "YYYY-MM-DD"]
 * @param {string} options.fromInput - hidden input for the start date
 * @param {string} options.toInput - hidden input for the end date
 * @param {Function} options.onChange - Callback called when dates change
 * @param {Function} options.onClose - Callback called when the picker closes
 * @param {Function} options.onReady - Callback called when the picker is ready
 * @param {Object} options.extraConfig - Additional configuration to merge
 * @returns {Object} Flatpickr instance
 */
export function initFlatpickrRange(element, options = {}) {
    var rootNode = element.getRootNode();

    if (!element) {
        console.error('Element not found:', selector);
        return null;
    }

    // Convert default dates to Date objects if necessary
    let defaultDates = null;
    if (options.defaultDate) {
        if (Array.isArray(options.defaultDate)) {
            defaultDates = options.defaultDate.map(date => {
                if (typeof date === 'string') {
                    return parseISODate(date);
                }
                return date;
            });
        } else if (typeof options.defaultDate === 'string') {
            const parts = options.defaultDate.split(' to ');
            if (parts.length === 2) {
                defaultDates = [parseISODate(parts[0]), parseISODate(parts[1])];
            }
        }
    }

    // Base configuration
    const config = {
        ...FLATPICKR_RANGE_CONFIG_FR,
        onReady: function(selectedDates, dateStr, instance) {
            // Initialize hidden inputs from the start
            if (selectedDates.length === 2) {
                if (options.fromInput) {
                    options.fromInput.value = getLocalDateString(selectedDates[0]);
                }
                if (options.toInput) {
                    options.toInput.value = getLocalDateString(selectedDates[1]);
                }
            }

            // Custom callback
            if (typeof options.onReady === 'function') {
                options.onReady(selectedDates, dateStr, instance);
            }
        },
        onChange: function(selectedDates, dateStr, instance) {
            // Update hidden inputs if specified
            if (selectedDates.length === 2) {
                if (options.fromInput) {
                    options.fromInput.value = getLocalDateString(selectedDates[0]);
                }
                if (options.toInput) {
                    options.toInput.value = getLocalDateString(selectedDates[1]);
                }
            }

            // Custom callback
            if (typeof options.onChange === 'function') {
                options.onChange(selectedDates, dateStr, instance);
            }
        },
        onOpen: function(selectedDates, dateStr, instance) {
            // Reset selection on open if the option is enabled
            if (options.clearOnOpen) {
                instance.clear();
                options.fromInput.value = '';
                options.toInput.value = '';
            }

            // Custom callback
            if (typeof options.onOpen === 'function') {
                options.onOpen(selectedDates, dateStr, instance);
            }
        },
        onClose: function(selectedDates, dateStr, instance) {
            // Custom callback
            if (typeof options.onClose === 'function') {
                options.onClose(selectedDates, dateStr, instance);
            }
        },
        ...options.extraConfig
    };

    // Add default dates if specified
    if (defaultDates) {
        config.defaultDate = defaultDates;
    }
    config.static = true;
    config.appendTo = element.parentElement;

    // config.positionElement = element;
    // config.position = "above";


    // Initialize Flatpickr
    const instance = flatpickr(element, config);

    // Flatpickr binds the calendar opening to BOTH "click" and "focus" on the input.
    // The "focus" binding makes the calendar reopen whenever the input regains focus -
    // e.g. when the browser window is refocused after clicking outside it - even though
    // the user never clicked the field. Remove the focus-to-open binding; clicking the
    // input still opens the picker as expected.
    if (instance && instance._input) {
        instance._input.removeEventListener("focus", instance.open);
    }

    // Auto destroy instance when element is removed from DOM
    const observer = new MutationObserver(() => {
        if (!element.isConnected) {
            try {
                instance.destroy();
            } finally {
                observer.disconnect();
            }
        }
    });
    observer.observe(rootNode, {
        childList: true,
        subtree: true
    });

    return instance;
}

/**
 * Updates the dates of an existing Flatpickr range
 *
 * @param {Object} instance - Flatpickr instance
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 */
function updateFlatpickrRange(instance, startDate, endDate) {
    if (instance) {
        instance.setDate([startDate, endDate]);
    }
}

/**
 * Retrieves the selected dates in ISO format
 *
 * @param {Object} instance - Flatpickr instance
 * @returns {Object} Object with from and to in YYYY-MM-DD format
 */
function getFlatpickrRangeDates(instance) {
    if (!instance || !instance.selectedDates || instance.selectedDates.length !== 2) {
        return { from: null, to: null };
    }

    return {
        from: getLocalDateString(instance.selectedDates[0]),
        to: getLocalDateString(instance.selectedDates[1])
    };
}
